import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Card,
  CheckboxGroupField,
  Field,
  Form,
  SkeletonLoader,
  useToastContext,
} from '@payaca/ambrosia';

import { FC, ReactNode, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { useReportUnsavedChanges } from './SettingsUnsavedChanges';
import { z } from 'zod/v4';
import useUpdateFieldServiceCustomFields from '../../../api/mutations/account/useUpdateFieldServiceCustomFields';
import useGetFieldServiceCustomFields from '../../../api/queries/me/useGetFieldServiceCustomFields';
import useGetProjectFieldsets from '../../../api/queries/me/useGetProjectFieldsets';

const fieldServiceCustomFieldsSchema = z.object({
  allowedCustomFieldIds: z.array(z.string()),
});
type FormType = z.infer<typeof fieldServiceCustomFieldsSchema>;

type Props = {
  readOnly: boolean;
};

const CompanySettingsFieldServiceCustomFields: FC<Props> = (props) => {
  const { readOnly } = props;
  const { pushSuccess } = useToastContext();

  const { data: allowedCustomFields } = useGetFieldServiceCustomFields();
  const { projectFieldsets } = useGetProjectFieldsets();
  const { mutateAsync: updateFieldServiceCustomFieldsMutation } =
    useUpdateFieldServiceCustomFields();

  const formMethods = useForm<FormType>({
    resolver: zodResolver(fieldServiceCustomFieldsSchema),
    defaultValues: {
      allowedCustomFieldIds: allowedCustomFields?.map((f) => f.id) || [],
    },
  });
  // history supports one navigation blocker; report to the section's shared
  // guard rather than mounting a competing Prompt.
  useReportUnsavedChanges(formMethods.formState.isDirty);

  useEffect(() => {
    if (allowedCustomFields) {
      formMethods.reset({
        allowedCustomFieldIds: allowedCustomFields.map((f) => f.id),
      });
    }
  }, [allowedCustomFields, formMethods]);

  const onSubmit = async (data: FormType) => {
    await updateFieldServiceCustomFieldsMutation({
      allowedCustomFieldIds: data.allowedCustomFieldIds,
    });
    pushSuccess('Saved successfully');
  };

  let content: ReactNode;
  if (!allowedCustomFields || !projectFieldsets) {
    content = <SkeletonLoader />;
  } else if (projectFieldsets.length === 0) {
    content = (
      <p className="text-gray-500">
        No project fieldsets have been created yet. You can create them in the
        custom fields settings.
      </p>
    );
  } else {
    content = (
      <Form methods={formMethods} onSubmit={onSubmit}>
        <Controller<FormType, 'allowedCustomFieldIds'>
          name="allowedCustomFieldIds"
          render={({ field: { name, ...attributes } }) => (
            <Field name={name}>
              <CheckboxGroupField
                horizontal
                cols={3}
                options={projectFieldsets.map((f) => ({
                  label: f.label,
                  value: f.id,
                }))}
                {...attributes}
              />
            </Field>
          )}
        />
        <div className="flex justify-end mt-4">
          <Button
            type="submit"
            disabled={readOnly || !formMethods.formState.isDirty}
          >
            Save
          </Button>
        </div>
      </Form>
    );
  }

  return (
    <Card sizeVariant="lg">
      <Card.Body className="space-y-4">
        <h2>Custom fieldsets</h2>
        <p>
          Choose which project fieldsets Field Agents can add to events
          themselves from the Field Service app.
        </p>
        {content}
      </Card.Body>
    </Card>
  );
};

export default CompanySettingsFieldServiceCustomFields;
